import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { FaPlus, FaSync, FaTrash } from 'react-icons/fa';
import { toast } from 'react-toastify';
import classNames from 'classnames';
import Button from '../../components/buttons/Button';
import WarningModal from '../../components/modals/WarningModal';
import AddExternalCalendarModal from '../../components/modals/AddExternalCalendarModal';
import calendarConnectionService from '../../../services/calendarConnectionService';
import { ExternalCalendar } from '../../../services/model/calendarConnection.model';
import { formatDateAndTime } from '../../../utils/stringUtils';
import { SubscriptionType } from '../../../types/misc';
import IconBadge from '../../components/icons/IconBadge';
import { eventTypeIcons } from '../../../utils/iconUtils';
import useSelectedBusiness from '../../../hooks/business/useSelectedBusiness';
import PrimaryOverlay from '../../components/overlays/PrimaryOverlay';
import PremiumRequiredCard from '../../components/cards/PremiumRequiredCard';

export default function ExternalCalendarsTab(): JSX.Element {
  const { t } = useTranslation();
  const business = useSelectedBusiness();
  const navigate = useNavigate();
  const [externalCalendars, setExternalCalendars] = useState<ExternalCalendar[]>([]);
  const [loading, setLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [calendarToDelete, setCalendarToDelete] = useState<number | null>(null);

  // Check if user has a free subscription
  const getActiveSubscription = (): SubscriptionType => {
    if (!business) return SubscriptionType.FREE;
    if (business.trialType && !business.subscription) {
      return business.trialType;
    }
    return business.subscription?.subscriptionType ?? SubscriptionType.FREE;
  };

  const activeSubscription = getActiveSubscription();
  const isFreeSubscription = activeSubscription === SubscriptionType.FREE;

  useEffect(() => {
    loadExternalCalendars();
  }, []);

  const loadExternalCalendars = async () => {
    try {
      setLoading(true);
      const calendars = await calendarConnectionService.getExternalCalendars();
      setExternalCalendars(calendars);
    } catch (error) {
      toast.error(t('page.event.calendar.external.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const handleCalendarAdded = () => {
    loadExternalCalendars();
  };

  const handleDeleteCalendar = async () => {
    if (calendarToDelete === null) return;
    
    try {
      await calendarConnectionService.deleteExternalCalendar(calendarToDelete);
      toast.success(t('page.event.calendar.external.deleteSuccess'));
      loadExternalCalendars();
    } catch (error) {
      toast.error(t('page.event.calendar.external.deleteError'));
    } finally {
      setDeleteModalOpen(false);
      setCalendarToDelete(null);
    }
  };

  const showDeleteConfirmation = (id: number) => {
    setCalendarToDelete(id);
    setDeleteModalOpen(true);
  };

  const handleFetchCalendar = async (id: number) => {
    if (isFetching) return;
    setIsFetching(true);
    try {
      await calendarConnectionService.fetchExternalCalendar(id);
      toast.success(t('page.event.calendar.external.fetchSuccess'));
      loadExternalCalendars();
    } catch (error) {
      toast.error(t('page.event.calendar.external.fetchError'));
    } finally {
      setIsFetching(false);
    }
  };

  return (
    <div className={classNames('w-full flex flex-col gap-4 relative', {
      'h-[80vh] overflow-hidden': isFreeSubscription,
    })}>
      {isFreeSubscription && (
        <PrimaryOverlay>
          <PremiumRequiredCard 
            description={t('page.event.calendar.external.premiumRequired')} 
            buttonText={t('general.upgrade')}
            onClick={() => navigate('/settings/subscriptions')}
            />
        </PrimaryOverlay>
      )}
      
      <div className="flex items-center justify-between">
        <h3 className="text-xl font-semibold text-primary-900">
          {t('page.event.calendar.external.calendars')}
        </h3>
        <Button
          variant="primary"
          onClick={() => setShowAddModal(true)}
          disabled={isFreeSubscription}
          className="flex items-center gap-2">
          <FaPlus className="h-4 w-4" />
          {t('page.event.calendar.external.addCalendar')}
        </Button>
      </div>

      {/* Calendar List */}
      <div className="flex flex-col gap-4">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="text-primary-600">
              {t('general.loading')}...
            </div>
          </div>
        ) : externalCalendars.length === 0 ? (
          <section className='mt-8 flex w-full flex-col items-center justify-center gap-4'>
            <img src="/assets/figures/mess.svg" alt="vera mess" className="w-1/8 max-md:w-1/4" />
            <h2 className="mt-2 text-center text-2xl">
              {t('page.event.calendar.external.noCalendars')}
            </h2>
            <p className="w-1/2 text-center text-gray-500 max-md:w-[80%]">
              {t('page.event.calendar.external.noCalendarsDescription')}
            </p>
          </section>
        ) : (
          <div className="space-y-3">
            {externalCalendars.map((calendar) => (
              <div
                key={calendar.id}
                className="flex items-center justify-between rounded-lg bg-secondary-200 p-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    {calendar.eventType && (
                      <IconBadge icon={eventTypeIcons[calendar.eventType]} className="rounded-[6px] p-1" />
                    )}
                    <div className="font-medium text-primary-900 truncate">
                      {calendar.url}
                    </div>
                    {calendar.eventType && (
                      <span className="text-sm text-primary-600">
                        ({t(`page.createEvent.type.type.${calendar.eventType.toLowerCase()}.title`)})
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-primary-600">
                    {t('page.event.calendar.external.lastFetched')}: {' '}
                    {calendar.lastFetchDate
                      ? formatDateAndTime(new Date(calendar.lastFetchDate))
                      : t('general.never')
                    }
                  </div>
                </div>
                <div className="flex gap-2 ml-4">
                  <Button
                    variant="secondary"
                    onClick={() => handleFetchCalendar(calendar.id)}
                    disabled={isFetching || isFreeSubscription}
                    className="flex items-center gap-1">
                    <FaSync className="h-4 w-4" />
                    {t('page.event.calendar.external.fetch')}
                  </Button>
                  <Button
                    variant="danger"
                    onClick={() => showDeleteConfirmation(calendar.id)}
                    disabled={isFreeSubscription}
                    className="flex items-center gap-1">
                    <FaTrash className="h-4 w-4" />
                    {t('general.delete')}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add External Calendar Modal */}
      <AddExternalCalendarModal
        open={showAddModal}
        setOpen={setShowAddModal}
        onCalendarAdded={handleCalendarAdded}
      />

      {/* Delete Confirmation Modal */}
      <WarningModal
        open={deleteModalOpen}
        setOpen={setDeleteModalOpen}
        title={t('page.event.calendar.external.deleteWarningTitle')}
        description={t('page.event.calendar.external.deleteWarningDescription')}
        button={t('general.delete')}
        onContinue={handleDeleteCalendar}
        saveClassName="bg-danger text-secondary-50"
      />
    </div>
  );
}
